import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import alertify from 'alertifyjs';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { ApplicationDetails, ApprovalStatus, WorkFlowHistoryItemResponse, WorkFlowHistorySearchResponse, WorkflowPaginationRequestPayload } from './approvals-landing.model';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { FilterDrawerComponent } from "../../../components/filter-drawer/filter-drawer.component";
import { FilterDrawerFieldConfig, FilterDrawerValue } from '../../../components/filter-drawer/filter-drawer.models';
import { HttpErrorResponse } from '@angular/common/http';
import { GenericPaginationResponseV2, GenericResponse } from '../../../modules/shared/models/general.modal';
import { MdaListResponse, MdaOption } from '../../../modules/services/utility-service.models';
import { VendorByIdApiResponse } from '../../vendors/vendor-landing/vendor-landing.model';
import { ApprovalOrQueryModal } from "../components/approval-or-query-modal/approval-or-query-modal.component";
import { UnderscorePipe } from '../../../modules/pipes/underscore-pipe.pipe';
import { Subscription } from 'rxjs';
import { WorkflowNotificationService } from '../../../modules/services/workflow-notification.service';

type DetailsTab = 'overview' | 'directors' | 'bank' | 'documents' | 'comments';

type ApprovalStatusType = "PENDING" | "APPROVED";

type ApprovalFilter = {
  id: string;
  name: string;
  mdaId: string;
  fromDate: string;
  toDate: string;
  worflowType: string;
  applicationId: string;
};

@Component({
  selector: 'app-approvals-landing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageHeaderComponent,
    FilterDrawerComponent,
    ApprovalOrQueryModal,
    UnderscorePipe
],
  templateUrl: './approvals-landing.component.html',
  styleUrl: './approvals-landing.component.scss',
})
export class ApprovalsLandingComponent implements OnInit, OnDestroy {
  tab: DetailsTab = 'overview';
  selectedApprovalStatus: ApprovalStatusType = "PENDING";
  pendingCount: number = 0;
  isLoadingWorkflowHistory: boolean = false;
  isLoadingApplicationDetails: boolean = false;

  applications: WorkFlowHistoryItemResponse[] = [];
  selectedApplication: WorkFlowHistoryItemResponse | null = this.applications[0] ?? null;
  applicationDetails: ApplicationDetails | null = null;

  isQueryModalOpen = false;
  queryMessage = '';
  showModal: boolean = false;
  mode: "APPROVE" | "QUERY" = "APPROVE";

  readonly emptyFilters: ApprovalFilter = {
    id: '',
    name: '',
    fromDate: '',
    mdaId: '',
    toDate: '',
    worflowType: '',
    applicationId: ''
  };

  approvalFilters: ApprovalFilter = { ...this.emptyFilters };
  approvalFilterFields: FilterDrawerFieldConfig[] = [];
  isFilterDrawerOpen = false;

  // readonly pageSize = 10;
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  mdaOptions: MdaOption[] = [];
  private workflowNotificationSubscription?: Subscription;


  constructor(
    private readonly apiService: ApiServiceService,
    private readonly workflowNotificationService: WorkflowNotificationService
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);
  }

  ngOnInit(): void {
    this.fetchWorkflowHistory();
    this.fetchPendingWorkflowCount();
    this.fetchMdas();
    this.workflowNotificationService.connect();
    this.workflowNotificationSubscription = this.workflowNotificationService.workflowNotifications$.subscribe((notification) => {
      if (!notification.type?.startsWith('workflow.')) return;
      this.fetchPendingWorkflowCount();
      this.fetchWorkflowHistory(this.currentPage);
    });
  }

  ngOnDestroy(): void {
    this.workflowNotificationSubscription?.unsubscribe();
  }

  fetchWorkflowHistory(page: number = 1, pageSize: number = 10) {
    let payload: Partial<WorkflowPaginationRequestPayload>  = {
      orderDirection: "DESC",
      workflowType: "VENDOR_APPLICATION",
      page,
      pageSize,
      approvalStatus: this.selectedApprovalStatus
    };

    if(this.approvalFilters.applicationId) payload.applicationId = this.approvalFilters.applicationId;
    if(this.approvalFilters.fromDate) payload.fromDate = this.approvalFilters.fromDate;
    if(this.approvalFilters.mdaId) payload.mdaId = this.approvalFilters.mdaId;
    if(this.approvalFilters.toDate) payload.toDate = this.approvalFilters.toDate;
    if(this.approvalFilters.worflowType) payload.workflowType = this.approvalFilters.worflowType;

    this.isLoadingWorkflowHistory = true;
    this.selectedApplication = null;
    this.apiService.post('/workflows/instance-history/search', payload).subscribe(
      {
        next: (response: GenericPaginationResponseV2<WorkFlowHistoryItemResponse>) => {
          if (response.status !== "success") {
            alertify.error(response.message || "Failed to get approval list");
            this.isLoadingWorkflowHistory = false;
            return;
          }
          this.applications = response.data.content;
          this.totalItems = response.data.total ?? this.applications.length;
          this.totalPages = (Math.ceil(response.data.total / pageSize)) ?? 1;
          this.currentPage = response.data.page;
          this.hasNextPage = response.data.page < this.totalPages;
          this.hasPreviousPage = this.currentPage > 1;
          this.selectApproval(this.applications[0]);
          this.buildVendorFilterFields();

          if (this.selectedApprovalStatus === "PENDING") {
            this.pendingCount = this.totalItems;
          }
          this.isLoadingWorkflowHistory = false;
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message);
          } else {
            alertify.error(JSON.stringify(error));
          }
          this.applications = [];
          this.isLoadingWorkflowHistory = false;
        }
      }
    );
  }

  fetchPendingWorkflowCount() {
    let payload: Partial<WorkflowPaginationRequestPayload>  = {
      orderDirection: "DESC",
      page: 1,
      pageSize: 0,
      approvalStatus: "PENDING"
    };

    this.isLoadingWorkflowHistory = true;
    this.apiService.post('/workflows/instance-history/search', payload).subscribe(
      {
        next: (response: GenericPaginationResponseV2<WorkFlowHistoryItemResponse>) => {
          if (response.status !== "success") {
            alertify.error(response.message || "Failed to get approval list");
            return;
          }

          this.pendingCount = response.data.total;
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message);
          } else {
            alertify.error(JSON.stringify(error));
          }
        }
      }
    );
  }

  fetchApplicationDetails() {
    this.isLoadingApplicationDetails = true;
    this.apiService.get(`/vendors/${this.selectedApplication?.applicationId}`).subscribe({
      next: (response: GenericResponse<VendorByIdApiResponse>) => {
        if (response.status !== "success") {
          alertify.error(response.message || "Failed to get application details");
          this.isLoadingApplicationDetails = false;
          return;
        }
        this.applicationDetails = this.mapVendorDetailsToApplicationDetails(response.data);
        this.isLoadingApplicationDetails = false;
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message || "Failed to get application details");
        } else {
          alertify.error(JSON.stringify(error) || "Failed to get application details");
        }
        this.isLoadingApplicationDetails = false;
      }
    })
  }

  mapVendorDetailsToApplicationDetails(vendor: VendorByIdApiResponse): ApplicationDetails {
    return {
      metaData: {
        applicationId: vendor.id,
        vendorReference: vendor.vendor_reference,
        vendorType: vendor.vendor_type,
        approvalStatus: vendor.approval_status,
        asin: vendor.asin,
        categoryName: vendor.category_name,
        name: vendor.name
      },
      overview: {
        phoneNumber: vendor.phone_no,
        address: vendor.address,
        businessClassification: vendor.business_classification,
        dateOfIncorporation: vendor.date_of_incorporation,
        mdaAssociation: vendor.mda_name,
        natureOfService: vendor.nature_of_service,
        rcNumber: vendor.rc_number,
        tccNumber: vendor.tcc_number,
        tin: vendor.tin,
        paymentAmount: vendor.payment_amount
      },
      bankDetails: {
        accountName: vendor.account.account_name,
        accountNumber: vendor.account.account_number,
        bank: vendor.account.bank_name
      },
      commments: vendor.comments.map(x => {
        return {
          comment: x.comment,
          createdAt: x.createdAt,
          user: x.user
        }
      }),
      directors: vendor.directors.map(x => {
        return {
          address: x.address,
          asin: x.asin,
          bvn: x.bvn,
          email: x.email,
          name: x.name,
          phoneNumber: x.phone_number
        }
      }),
      documents: vendor.documents.map(x => {
        return {
          name: x.document_name,
          type: "PDF",
          uploadedAt: x.created_at,
          status: x.status,
          url: x.uploaded_document_url
        }
      })
    }
  }

  fetchMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];
        this.buildVendorFilterFields();
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(error);
        }
      },
    });
  }

  approveApplication() {
    this.showModal = true;
    this.mode = "APPROVE";
  }

  queryApplication() {
    this.showModal = true;
    this.mode = "QUERY";
  }

  setSelectedApprovalStatus(status: ApprovalStatusType) {
    this.selectedApprovalStatus = status;
    this.fetchWorkflowHistory();
  }

  selectApproval(row: WorkFlowHistoryItemResponse): void {
    if (row) {
      this.selectedApplication = row;
      this.tab = 'overview';
      this.fetchApplicationDetails();
    } else {
      this.selectedApplication = null;
    }
  }

  getApplicationStatusPillColor(applicationStatus: string): string {
    switch(applicationStatus) {
      case "APPROVED":
        return "!bg-[#008000] !text-white";
      case "PENDING":
        return "!bg-[#C38513] !text-white";
      case "QUERIED":
        return "!bg-[#D91818] !text-white";
      default:
        return ""
    }
  }

   getApplicationStatusColor(applicationStatus: string): string {
    switch(applicationStatus) {
      case "APPROVED":
        return "!text-[#008000]";
      case "PENDING":
        return "!text-[#C38513]";
      case "QUERIED":
        return "!text-[#D91818]";
      default:
        return ""
    }
  }

  getPaymentStatusColor(paymentStatus: string): string {
    switch(paymentStatus) {
      case "PAID":
        return "!bg-[#E6FFE9] !text-[#289D35]";
      case "PENDING":
        return "!bg-[#FFF6D4] !text-[#A88205]";
      default:
        return ""
    }
  }

  formatCurrency(value: number, currencyCode: string = "NGN", locale: string = "en-NG"): string {
    return Intl.NumberFormat("en-NG", {
        currency: currencyCode,
        style: 'currency'
      }).format(value);
  }

  maskBvn(value: string): string {
    const raw = String(value ?? '').trim();
    if (!raw) return 'N/A';
    return `******${raw.slice(-4)}`;
  }

  approveSelected(): void {
    const current = this.selectedApplication;
    if (!current) return;
  }

  rejectSelected(): void {
    const current = this.selectedApplication;
    if (!current) return;
  }

  handleModalSubmission() {
    this.showModal = false;
    this.fetchWorkflowHistory();
  }

  openQueryModal(): void {
    if (!this.selectedApplication) return;
    this.queryMessage = '';
    this.isQueryModalOpen = true;
  }

  closeQueryModal(): void {
    this.isQueryModalOpen = false;
    this.queryMessage = '';
  }

  submitQuery(): void {
    const current = this.selectedApplication;
    if (!current) return;
    const message = this.queryMessage.trim();
    if (!message) {
      alertify.message('Please enter a query message.');
      return;
    }
    this.closeQueryModal();
  }

  get activeFilterCount(): number {
    return Object.entries(this.approvalFilters).filter(([key, value]) => {
      return String(value ?? '').trim().length > 0;
    }).length;
  }

  applyFilters(raw: FilterDrawerValue): void {
    this.approvalFilters = {
      ...this.emptyFilters,
      ...Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [key, `${value ?? ''}`]),
      ),
    } as ApprovalFilter;

    // const rawApprovalStatus = (raw['approvalStatus'] ?? 'ALL') as string;
    const rawPaymentStatus = (raw['paymentStatus'] ?? 'ALL') as string;
    // this.approvalFilters.approvalStatus = rawApprovalStatus || 'ALL';
    // this.approvalFilters.paymentStatus = rawPaymentStatus || 'ALL';

    this.isFilterDrawerOpen = false;
    this.fetchWorkflowHistory(1);
  }

  closeFilters(): void {
    this.isFilterDrawerOpen = false;
  }

  openFilters(): void {
    this.isFilterDrawerOpen = true;
  }

   private buildVendorFilterFields(): void {
    const mdaOptions = Array.from(
      new Map(
        this.mdaOptions?.map((mda) => [
          mda.id,
          { label: mda.name || mda.id, value: mda.id },
        ]),
      ).values(),
    );

    const approvalOptions = Array.from(
      ["APPROVED", "PENDING", "QUERIED"],
    ).sort((a, b) => a.localeCompare(b));

    const paymentStatusOptions = Array.from(
      ["PAID", "PENDING"]
    ).sort((a, b) => a.localeCompare(b));

    this.approvalFilterFields = [
      {
        key: 'applicationId',
        label: 'Application ID',
        type: 'text',
        placeholder: 'Filter by application ID',
      },
      {
        key: 'applicantName',
        label: 'Applicant Name',
        type: 'text',
        placeholder: 'Filter by applicant name',
      },
      {
        key: 'mdaId',
        label: 'MDA',
        type: 'searchable-select',
        placeholder: 'Select MDA',
        searchablePlaceholder: 'Search MDA options',
        options: mdaOptions,
        initialValue: this.approvalFilters.mdaId
      },
      {
        key: 'fromData',
        label: 'From Date (Created)',
        type: 'date',
        // placeholder: 'Select MDA',
      },
      {
        key: 'toData',
        label: 'To Date (Created)',
        type: 'date',
        max: new Date().toDateString()
        // placeholder: 'Select MDA',
      },
    ];
  }

  goToPreviousPage(): void {
    if (!this.hasPreviousPage || this.isLoadingWorkflowHistory) {
      return;
    }

    this.fetchWorkflowHistory(this.currentPage - 1);
  }

  goToNextPage(): void {
    if (!this.hasNextPage || this.isLoadingWorkflowHistory) {
      return;
    }

    this.fetchWorkflowHistory(this.currentPage + 1);
  }
}
